import React from "react";
import { ProductFruits, useProductFruitsApi } from "react-product-fruits";
import { useQuery } from "react-query";
import { useNavigate } from "react-router-dom";

import { useResponsive } from "ahooks";

import { fetchUserInfo } from "@api/productFruits";
import { useAuth } from "@hooks";
import { useConfigurationStore, useUIStore } from "@stores";

import { WORKSPACE_CODE } from "@components/ProductFruits/constants";
import queryKeys from "@constants/queryKeys";

import PopupChecklist from "./components/PopupChecklist/PopupChecklist";
import { registerEvents } from "./helpers/registerEvents";
import { useChecklistInjection } from "./hooks/useChecklistInjection";
import { ProductFruitsApi } from "./types";

const ProductFruitsProvider: React.FC = () => {
  const { isAuthenticated } = useAuth();

  const { domainSettings } = useConfigurationStore();

  const { is_pf_enabled: isProductFruitsEnabled = false } = domainSettings || {};

  const {
    showProductFruitsChecklist,
    setShowProductFruitsChecklist,
    setIsProductFruitsSwitchToAdminTourRunning,
  } = useUIStore();

  const checklistContentRef = React.useRef<HTMLDivElement | null>(null);

  const productFruitsRef = React.useRef<HTMLDivElement | null>(null);

  const { md } = useResponsive();

  const navigate = useNavigate();

  const { data: productFruitsUserInfoData, isSuccess: hasProductFruitsLoaded } = useQuery(
    [queryKeys.productFruits.userInfo],
    fetchUserInfo,
    {
      enabled: isAuthenticated && isProductFruitsEnabled,
      select: (data) => data?._data,
      refetchOnWindowFocus: false,
    },
  );

  useProductFruitsApi((api: ProductFruitsApi) => {
    registerEvents(
      api,
      setShowProductFruitsChecklist,
      setIsProductFruitsSwitchToAdminTourRunning,
      navigate,
    );
  }, []);

  useChecklistInjection(checklistContentRef, productFruitsRef, productFruitsUserInfoData);

  return (
    <>
      {productFruitsUserInfoData && (
        <div ref={productFruitsRef}>
          <ProductFruits
            workspaceCode={WORKSPACE_CODE}
            language="en"
            user={productFruitsUserInfoData}
          />
        </div>
      )}

      {hasProductFruitsLoaded && productFruitsUserInfoData && (
        <PopupChecklist
          isOpen={showProductFruitsChecklist && md}
          onClose={(): void => setShowProductFruitsChecklist(false)}
        >
          <div ref={checklistContentRef} className="popup-content" />
        </PopupChecklist>
      )}
    </>
  );
};

export default ProductFruitsProvider;
